import React, { useState, useEffect } from "react";
import s from "./OrdenesPedido.module.css";

//servicios
import ordersService from "../../../api/delivery/ordersServices";
import deliveryServices from "../../../api/delivery/DeliveryService";

//assets
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { dateFormats } from "../../../helpers/convertDate";
import { CreateOrder } from "./Create";
import Pager from "../../Shared/Pagination/Pagination";
import OrdersSearch from "./Search";
import Swal from "sweetalert2";

//Material
import {
  Button,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import LocalPrintshopRoundedIcon from "@mui/icons-material/LocalPrintshopRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SellIcon from "@mui/icons-material/Sell";
import { formatJavaCompletossx } from "../../../config/strings";
import { useNotificationContext } from "../../../components/Notifications/CustomNotifications";

export const OrdenesPedido = () => {
  const notification = useNotificationContext();

  const dates = dateFormats();

  const { t } = useTranslation();
  const { textColor } = useSelector((state) => state.local);

  let navigate = useNavigate();
  let location = useLocation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);

  //Paginación
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageCustom, setPageCustom] = useState(1);
  const [filters, setFilters] = useState({
    orderId: 0,
    supplierId: 0,
    gameId: 0,
  });
  const [newFilters, setNewFilters] = useState({
    orderId: 0,
    supplierId: 0,
    gameId: 0,
  });
  const [sort, setSort] = useState({
    criteria: "DESC",
    field: "dateCreate",
  });
  const [edit, setEdit] = useState({
    status: false,
    item: {},
  });

  const newOrder = () => {
    const path = "./create";
    navigate(path);
  };

  const handleNavigate = (id) => {
    const path = `./details/${id}`;
    navigate(path);
  };

  const handlePrint = (item) => {
    let path = `./impreso/${item.id}`;
    navigate(path);
  };

  const handleEdit = (remito) => {
    setEdit({
      status: true,
      item: remito,
    });
    navigate("./create");
  };

  const fetchDeliveryNotesForOrder = async (item) => {
    const response = await ordersService.getOrderById(item.id);

    return response;
  };

  const fetchOrders = () => {
    setLoading(true);
    ordersService
      .getAllOrders(page, pageSize, sort)
      .then((res) => {
        setTotalPages(res.totalPages);
        setItems(res.content);
      })
      .then(() => setLoading(false))
      .catch((error) => {
        setLoading(false);
        console.log(error);
        notification.error("Error endpoint", error.message);
      });
  };

  useEffect(() => {
    if (searching) {
      setLoading(true);
      ordersService
        .searchOrder(page, pageSize, newFilters, sort)
        .then((res) => {
          if (res) {
            setTotalPages(res.totalPages);
            setItems(
              res.content.filter(function (item, index, self) {
                return (
                  self.findIndex(
                    (p) => JSON.stringify(p) === JSON.stringify(item),
                  ) === index
                );
              }),
            );
          } else {
            setTotalPages(0);
            setItems([]);
          }
        })
        .then(() => setLoading(false))
        .catch((error) => {
          setLoading(false);
          console.log(error);
          notification.error("Error endpoint", error.message);
        });
    } else {
      fetchOrders();
    }
  }, [page, pageSize, searching, sort]);

  const handleSort = (item) => {
    setSort((prev) => ({
      criteria:
        prev.field === item
          ? prev.criteria === "DESC"
            ? "ASC"
            : "DESC"
          : "DESC",
      field: item,
    }));
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: `${t("deleteOrder")}: ${item.id.toString().padStart(5, "0")}`,
      text: `${t("confirmDeleteOrder")}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00A36C",
      cancelButtonColor: "#d33",
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        fetchDeliveryNotesForOrder(item)
          .then((notes) => {
            ordersService
              .deleteOrder(notes.id)
              .then(() => {
                Swal.fire(t("deleted"), t("orderDeleteDone"), "success");
                setLoading(true);
                fetchOrders();
              })
              .catch((error) => {
                setLoading(false);
                notification.error("Error endpoint", error.message);
              });
          })
          .catch((error) => {
            Swal.fire(
              `${t("purchaseOrder")} ${item.id.toString().padStart(5, "0")}`,
              `${t("orderDeleteDeliveryNotes")}`,
              "warning",
            );
          });
      }
    });
  };

  const cellStyles = {
    fontFamily: "Roboto, sans-serif",
    fontSize: "0.9rem",
    fontWeight: "400",
    borderBottom: "1px solid rgba(0, 0, 0, 0.455)",
    cursor: "pointer",
  };

  return location.pathname.includes("create") ? (
    <CreateOrder
      sort={sort}
      edit={edit}
      setEdit={setEdit}
      setPage={setPage}
      setItems={setItems}
      setTotalPages={setTotalPages}
    />
  ) : (
    <Grid container className={s.container}>
      <Grid item container xs={11} className={s.header}>
        <Grid item lg={6} md={6} xs={12} className={s.titleContainer}>
          <SellIcon sx={{ fontSize: "1.8rem", color: "primary.main" }} />
          <h4>{t("purchaseOrders")}</h4>
          <Button
            onClick={newOrder}
            variant="contained"
            className={s.add_btn}
            sx={{ color: textColor }}
          >
            {t("add")}
          </Button>
        </Grid>
        <Grid item xs={12} md={4} lg={4} className={s.filters}>
          <Button
            variant="contained"
            className={s.filterButton}
            onClick={() => setOpen(true)}
          >
            <TuneRoundedIcon
              sx={{ color: "secondary.light", fontSize: "1.8rem" }}
            />
            <p>{t("filter")}</p>
          </Button>
          {searching ? (
            <Button
              variant="contained"
              className={s.filterButton}
              onClick={() => {
                setFilters({
                  orderId: 0,
                  supplierId: 0,
                  gameId: 0,
                });
                setNewFilters({
                  orderId: 0,
                  supplierId: 0,
                  gameId: 0,
                });
                setSearching(false);
              }}
            >
              <p>{t("clearFilters")}</p>
            </Button>
          ) : null}
        </Grid>
      </Grid>
      {loading ? (
        <Grid item xs={11} className={s.loader}>
          <CircularProgress size={"3rem"} />
        </Grid>
      ) : items?.length ? (
        <Grid item container xs={11} className={s.data}>
          <TableContainer className={s.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                    onClick={() => handleSort("status")}
                  >
                    {`${t("type")} ${sort.field === "status" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                    onClick={() => handleSort("id")}
                  >
                    {`${t("orderNumber")} ${sort.field === "id" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                    onClick={() => handleSort("supplier.businessName")}
                  >
                    {`${t("provider")} ${sort.field === "supplier.name" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                    onClick={() => handleSort("dateCreate")}
                  >
                    {`${t("date")} ${sort.field === "createDate" ? (sort.criteria === "DESC" ? "▼" : "▲") : ""}`}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                  >
                    {t("edit")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                  >
                    {t("delete")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "primary.main",
                      color: textColor,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      fontFamily: "Roboto, sans-Serif",
                    }}
                  >
                    {t("print")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items?.map((item, i) => {
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow
                        sx={{
                          "&:hover": { backgroundColor: "secondary.light" },
                        }}
                      >
                        <TableCell
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                          onClick={() => handleNavigate(item.id)}
                        >
                          {item.status || "-"}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                          onClick={() => handleNavigate(item.id)}
                        >
                          {item.id.toString().padStart(5, "0")}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                          onClick={() => handleNavigate(item.id)}
                        >
                          {item.supplier?.businessName}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                          onClick={() => handleNavigate(item.id)}
                        >
                          {dates.formatLocalDate(
                            item.dateCreate,
                            formatJavaCompletossx,
                          ) || "-"}
                        </TableCell>
                        <TableCell
                          style={{ width: "10%" }}
                          align="center"
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                        >
                          {item.status === "Enviado" ? (
                            <EditRoundedIcon
                              sx={{
                                color: "grey",
                                fontSize: "1.8rem",
                                cursor: "not-allowed",
                              }}
                            />
                          ) : (
                            <EditRoundedIcon
                              className={s.iconbtn}
                              sx={{ color: "primary.dark" }}
                              onClick={() => handleEdit(item)}
                            />
                          )}
                        </TableCell>
                        <TableCell
                          style={{ width: "10%" }}
                          align="center"
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                        >
                          {item.status === "Enviado" ? (
                            <DeleteForeverRoundedIcon
                              sx={{
                                color: "grey",
                                fontSize: "1.8rem",
                                cursor: "not-allowed",
                              }}
                            />
                          ) : (
                            <DeleteForeverRoundedIcon
                              className={s.iconbtn}
                              sx={{ color: "primary.dark" }}
                              onClick={() => handleDelete(item)}
                            />
                          )}
                        </TableCell>
                        <TableCell
                          style={{ width: "10%" }}
                          align="center"
                          sx={{
                            ...cellStyles,
                            backgroundColor:
                              i % 2 === 0 ? "#dbdbdb76" : "#dbdbdd12",
                          }}
                        >
                          <LocalPrintshopRoundedIcon
                            onClick={() => handlePrint(item)}
                            className={s.iconbtn}
                            sx={{ color: "primary.dark" }}
                          />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: "50px",
            }}
          >
            <Pager
              pageSize={pageSize}
              setPageSize={setPageSize}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              pageCustom={pageCustom}
              setPageCustom={setPageCustom}
            />
          </Grid>
        </Grid>
      ) : searching ? (
        <Grid item xs={12} className={s.noResults}>
          {t("noResults")}
        </Grid>
      ) : (
        <Grid item xs={12} className={s.noResults}>
          {t("noResults")}
        </Grid>
      )}
      {open ? (
        <OrdersSearch
          setPage={setPage}
          open={open}
          setOpen={setOpen}
          setSearching={setSearching}
          setFilters={setFilters}
          filters={filters}
          setNewFilters={setNewFilters}
        />
      ) : null}
    </Grid>
  );
};
